/**
 * Product Media Embla Slider
 * Synced main slider + thumbnail slider for mobile product gallery
 * 
 * =====================================================================
 * DEV NOTE: How to update slide when variant changes
 * =====================================================================
 * 
 * The Embla instance is exposed on the slider container element.
 * You can access it and scroll to a specific slide by media ID or index.
 * 
 * OPTION 1: Scroll to slide by index
 * ----------------------------------
 * const sliderRoot = document.querySelector('[data-product-media-slider]');
 * if (sliderRoot && sliderRoot.emblaMain) {
 *   sliderRoot.emblaMain.scrollTo(0); // Scroll to first slide (index 0)
 * }
 * 
 * OPTION 2: Scroll to slide by media ID (variant image)
 * ------------------------------------------------------
 * function scrollToVariantImage(mediaId) {
 *   const sliderRoot = document.querySelector('[data-product-media-slider]');
 *   if (!sliderRoot || !sliderRoot.emblaMain) return;
 *   
 *   const slides = sliderRoot.querySelectorAll('[data-embla-main] .embla__slide');
 *   slides.forEach((slide, index) => {
 *     if (slide.dataset.mediaId == mediaId) {
 *       sliderRoot.emblaMain.scrollTo(index);
 *     }
 *   });
 * }
 * 
 * // Usage: Call when variant changes
 * document.addEventListener('variant:change', (e) => {
 *   const variant = e.detail.variant;
 *   if (variant && variant.featured_media) {
 *     scrollToVariantImage(variant.featured_media.id);
 *   }
 * });
 * 
 * OPTION 3: Scroll by variant title (using data-variant-title attribute)
 * ----------------------------------------------------------------------
 * function scrollToVariantByTitle(variantTitle) {
 *   const sliderRoot = document.querySelector('[data-product-media-slider]');
 *   if (!sliderRoot || !sliderRoot.emblaMain) return;
 *   
 *   const slides = sliderRoot.querySelectorAll('[data-embla-main] .embla__slide');
 *   slides.forEach((slide, index) => {
 *     const img = slide.querySelector('[data-variant-title]');
 *     if (img && img.dataset.variantTitle === variantTitle) {
 *       sliderRoot.emblaMain.scrollTo(index);
 *     }
 *   });
 * }
 * 
 * =====================================================================
 */

;(function () {
  if (typeof EmblaCarousel === 'undefined') {
    console.error('[ProductMediaEmbla] EmblaCarousel is undefined');
    return;
  }

  const initProductMediaSlider = (root) => {
    const mainEl = root.querySelector('[data-embla-main]');
    const thumbsEl = root.querySelector('[data-embla-thumbs]');
    
    if (!mainEl) {
      console.warn('[ProductMediaEmbla] Main slider element not found');
      return;
    }

    const mainViewport = mainEl.querySelector('[data-embla-main-viewport]');
    if (!mainViewport) {
      console.warn('[ProductMediaEmbla] Main viewport not found');
      return;
    }

    // Initialize main slider (1 slide per view)
    const mainEmbla = EmblaCarousel(mainViewport, {
      align: 'start',
      loop: false,
      containScroll: 'trimSnaps',
      dragFree: false,
      slidesToScroll: 1
    });

    // Navigation buttons
    const prevBtn = root.querySelector('[data-embla-main-prev]');
    const nextBtn = root.querySelector('[data-embla-main-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => mainEmbla.scrollPrev(), { passive: true });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => mainEmbla.scrollNext(), { passive: true });
    }

    // Update navigation buttons state
    const updateNavButtons = () => {
      if (prevBtn) {
        prevBtn.disabled = !mainEmbla.canScrollPrev();
        prevBtn.classList.toggle('is-disabled', !mainEmbla.canScrollPrev());
      }
      if (nextBtn) {
        nextBtn.disabled = !mainEmbla.canScrollNext();
        nextBtn.classList.toggle('is-disabled', !mainEmbla.canScrollNext());
      }
    };

    mainEmbla.on('select', updateNavButtons);
    mainEmbla.on('reInit', updateNavButtons);
    updateNavButtons();

    // Initialize thumbnails slider if present
    if (thumbsEl) {
      const thumbsViewport = thumbsEl.querySelector('[data-embla-thumbs-viewport]');
      if (!thumbsViewport) return;

      const thumbsEmbla = EmblaCarousel(thumbsViewport, {
        align: 'start',
        loop: false,
        containScroll: 'keepSnaps',
        dragFree: true,
        slidesToScroll: 1
      });

      const thumbSlides = thumbsEl.querySelectorAll('.embla__slide--thumb');
      
      // Update selected thumb
      const updateSelectedThumb = () => {
        const selectedIndex = mainEmbla.selectedScrollSnap();
        thumbSlides.forEach((thumb, index) => {
          const isSelected = index === selectedIndex;
          thumb.classList.toggle('is-selected', isSelected);
          thumb.classList.toggle('embla__slide--selected', isSelected);
        });
        
        // Scroll thumb into view if needed
        if (thumbsEmbla.slideNodes()[selectedIndex]) {
          thumbsEmbla.scrollTo(selectedIndex);
        }
      };

      // Click on thumb -> go to slide
      thumbSlides.forEach((thumb, index) => {
        const btn = thumb.querySelector('button');
        if (btn) {
          btn.addEventListener('click', () => {
            mainEmbla.scrollTo(index);
          }, { passive: true });
        }
      });

      // Sync: main slider changes -> update thumbs
      mainEmbla.on('select', updateSelectedThumb);
      mainEmbla.on('reInit', updateSelectedThumb);
      updateSelectedThumb();

      // Expose for debugging
      thumbsEl.embla = thumbsEmbla;
    }

    // Expose for debugging
    mainEl.embla = mainEmbla;
    root.emblaMain = mainEmbla;
  };

  const initAll = () => {
    const sliders = document.querySelectorAll('[data-product-media-slider]');
    
    if (sliders.length && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !entry.target.emblaMain) {
            initProductMediaSlider(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '200px' });
      
      sliders.forEach(node => observer.observe(node));
    } else {
      sliders.forEach(node => initProductMediaSlider(node));
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    const slider = event.target.querySelector('[data-product-media-slider]');
    if (slider && !slider.emblaMain) {
      initProductMediaSlider(slider);
    }
  });
})();
